import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Leaf, ShieldCheck, Ship, Award } from "lucide-react";

const ABOUT_IMG =
  "https://images.pexels.com/photos/33315751/pexels-photo-33315751.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=1400";

/** Numbered manifesto chapters — the editorial anchor of the site */
const CHAPTERS = [
  {
    n: "01",
    title: "Sourced at Origin",
    body: "Long-standing relationships with growers across Maharashtra, Karnataka and Tamil Nadu ensure consistent quality at origin — not corrected later on a lab bench.",
    icon: Leaf,
  },
  {
    n: "02",
    title: "Tested, Not Trusted",
    body: "Every lot passes moisture, pesticide-residue and microbial testing to meet EU, US and GCC import standards before it ever leaves our warehouse.",
    icon: ShieldCheck,
  },
  {
    n: "03",
    title: "Shipped, Not Promised",
    body: "FCL & LCL out of JNPT and Mundra. Documentation, customs and predictable transit windows handled by our in-house export team.",
    icon: Ship,
  },
  {
    n: "04",
    title: "Packed to Your Brief",
    body: "From 25 kg jute sacks to private-label retail packs — we package to your spec, your labels, your regulations.",
    icon: Award,
  },
];

export const About = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section
      id="about"
      ref={ref}
      className="relative bg-[#FAF9F6] pt-24 md:pt-32 pb-24 md:pb-32 overflow-hidden"
      data-testid="about-section"
    >
      {/* Slow editorial marquee */}
      <div className="pointer-events-none select-none border-y border-black/5 py-6 mb-24 overflow-hidden">
        <motion.div
          className="flex gap-16 whitespace-nowrap will-change-transform"
          animate={{ x: ["0%", "-50%"] }}
          transition={{ duration: 45, repeat: Infinity, ease: "linear" }}
        >
          {[...Array(2)].map((_, dup) => (
            <div key={dup} className="flex items-center gap-16">
              {[
                "Haldi",
                "Spices",
                "Onion Powder",
                "Coconut Shell",
                "Dehydrated Veg",
                "Turmeric",
                "Cumin",
                "Chilli",
                "Coriander",
                "Pepper",
              ].map((w, i) => (
                <span
                  key={`${dup}-${i}`}
                  className="font-display italic font-medium text-[#0A192F]/25"
                  style={{ fontSize: "clamp(2.5rem, 6vw, 5rem)", letterSpacing: "-0.02em" }}
                >
                  {w}
                  <span className="text-[#D4AF37] not-italic mx-6">◆</span>
                </span>
              ))}
            </div>
          ))}
        </motion.div>
      </div>

      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9, ease: [0.19, 1, 0.22, 1] }}
          className="flex items-center gap-4 mb-6"
        >
          <span className="h-px w-16 bg-[#D4AF37]" />
          <span className="font-accent text-[10px] text-[#475569]">
            The Manifesto
          </span>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <div className="lg:col-span-6">
            <motion.h2
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 1, ease: [0.19, 1, 0.22, 1] }}
              className="font-display font-bold text-[#0A192F] leading-[0.95] tracking-tight"
              data-testid="about-heading"
              style={{ fontSize: "clamp(2.5rem, 5.5vw, 5rem)" }}
            >
              We don't just export.
              <br />
              <span className="italic font-normal text-[#D4AF37]">We take ownership.</span>
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, delay: 0.15 }}
              className="mt-8 text-[#475569] text-base md:text-lg leading-relaxed max-w-xl"
            >
              Headquartered in Chinchwad, Pune, Rahilaxmi Exports LLP specialises
              in Indian agricultural commodities — Haldi, spices, dehydrated
              vegetables, onion powder and industrial coconut shell powder — for
              buyers who cannot afford a bad shipment.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, delay: 0.25 }}
              className="mt-10 border-l-2 border-[#D4AF37] pl-6"
            >
              <p className="font-display italic text-xl md:text-2xl text-[#0A192F] leading-snug">
                &ldquo;Honest sourcing. Tight quality control. Predictable
                deliveries.&rdquo;
              </p>
              <p className="mt-3 font-accent text-[10px] text-[#475569]">
                — Our promise to every buyer
              </p>
            </motion.div>
          </div>

          {/* Clipped, spotlit editorial image */}
          <div className="lg:col-span-6 relative">
            <motion.div
              initial={{ opacity: 0, scale: 0.94 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1.2, ease: [0.19, 1, 0.22, 1] }}
              className="relative overflow-hidden"
              style={{ clipPath: "polygon(0 0, 100% 4%, 100% 100%, 3% 96%)" }}
            >
              <motion.img
                src={ABOUT_IMG}
                alt="Cargo container shipping"
                className="w-full h-[460px] md:h-[600px] object-cover"
                style={{ y: imgY, scale: 1.1 }}
                data-testid="about-image-main"
              />
              {/* Spotlight */}
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  background:
                    "radial-gradient(60% 55% at 60% 45%, transparent 0%, rgba(6,15,30,0.55) 100%)",
                }}
              />
              <div className="absolute top-5 left-5 bg-[#D4AF37] text-[#0A192F] px-4 py-2 font-accent text-[10px]">
                Pune · Maharashtra · India
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1, delay: 0.2 }}
              className="absolute -bottom-6 -left-4 md:-left-8 bg-[#0A192F] text-white px-8 py-6 max-w-xs hidden md:block"
            >
              <div className="font-display text-4xl font-bold text-[#D4AF37]">
                25<span className="text-white">+</span>
              </div>
              <div className="font-accent text-[10px] text-white/70 mt-2">
                Countries served
              </div>
              <div className="text-xs text-white/80 mt-2 leading-relaxed">
                Active buyers across the EU, GCC, USA, UK and South-East Asia.
              </div>
            </motion.div>
          </div>
        </div>

        {/* Numbered chapters */}
        <div className="mt-32">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9 }}
            className="flex items-end justify-between mb-14 flex-wrap gap-4"
          >
            <h3
              className="font-display font-bold text-[#0A192F] leading-tight tracking-tight max-w-xl"
              style={{ fontSize: "clamp(1.75rem, 3.2vw, 2.75rem)" }}
            >
              Four chapters. One standard
              <span className="text-[#D4AF37]">.</span>
            </h3>
            <span className="gold-divider w-40" />
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#E2E8F0]">
            {CHAPTERS.map((ch, i) => {
              const Icon = ch.icon;
              return (
                <motion.article
                  key={ch.n}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{
                    duration: 0.9,
                    delay: i * 0.1,
                    ease: [0.19, 1, 0.22, 1],
                  }}
                  className="group relative bg-white p-10 md:p-14 transition-colors duration-700 hover:bg-[#0A192F]"
                  data-testid={`about-chapter-${ch.n}`}
                >
                  <div className="flex items-start justify-between mb-8">
                    <span
                      className="font-display font-bold text-[#0A192F]/15 group-hover:text-[#D4AF37]/40 transition-colors duration-700"
                      style={{ fontSize: "clamp(3rem, 5vw, 4.5rem)", lineHeight: 1 }}
                    >
                      {ch.n}
                    </span>
                    <Icon
                      size={26}
                      strokeWidth={1.4}
                      className="text-[#0A192F] group-hover:text-[#D4AF37] transition-colors duration-700"
                    />
                  </div>
                  <h4 className="font-display text-2xl md:text-3xl font-bold text-[#0A192F] group-hover:text-white transition-colors duration-700">
                    {ch.title}
                  </h4>
                  <p className="mt-5 text-[#475569] group-hover:text-white/70 transition-colors duration-700 leading-relaxed max-w-md">
                    {ch.body}
                  </p>

                  <motion.span
                    className="absolute bottom-0 left-0 h-[2px] w-0 bg-[#D4AF37] group-hover:w-full transition-all duration-700"
                    aria-hidden="true"
                  />
                </motion.article>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
